/**
 * use-pomodoro-finish — root-level watcher for the Pomodoro timer.
 *
 * Mounted once in `__root.tsx` so a running timer finishes (chime +
 * notification) wherever the user is in the app, and mirrors the countdown
 * into `document.title` while running/paused.
 */

import { useEffect } from 'react';
import { POMODORO_STORAGE_KEY, usePomodoroStore } from './pomodoro-store';
import {
  MODE_LABELS,
  formatClock,
  isFinished,
  remainingAt,
  type PomodoroMode,
} from './pomodoro-timer';
import { playChime } from './chime';

const FINISH_TITLES: Record<PomodoroMode, string> = {
  pomodoro: 'Pomodoro finished',
  short: 'Short break over',
  long: 'Long break over',
};

/**
 * Ask for notification permission, only while it is still undecided. Call from
 * a user gesture (the first Start click) — browsers ignore it otherwise.
 */
export function requestNotificationPermissionOnce(): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') void Notification.requestPermission();
  } catch {
    /* notifications are best-effort */
  }
}

function persistedRunMatches(endsAt: number | null): boolean {
  try {
    const raw = localStorage.getItem(POMODORO_STORAGE_KEY);
    if (!raw) return true;
    const persisted = (JSON.parse(raw) as { state?: { status?: string; endsAt?: number | null } })
      .state;
    return persisted?.status === 'running' && persisted.endsAt === endsAt;
  } catch {
    return true;
  }
}

function notifyFinished(mode: PomodoroMode): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    new Notification(FINISH_TITLES[mode]);
  } catch {
    /* some browsers (Android Chrome) require a service worker — ignore */
  }
}

export function usePomodoroFinish(): void {
  const status = usePomodoroStore((s) => s.status);
  const mode = usePomodoroStore((s) => s.mode);
  const endsAt = usePomodoroStore((s) => s.endsAt);

  // Finish detection. Runs immediately on mount so a reload past `endsAt`
  // finishes right away, then once a second while running.
  useEffect(() => {
    if (status !== 'running') return;
    const check = () => {
      const s = usePomodoroStore.getState();
      if (!isFinished(s, Date.now())) return;
      // Another tab may have finished (or paused) this run a moment ago.
      if (!persistedRunMatches(s.endsAt)) return;
      const finishedMode = s.mode;
      s.finish();
      playChime();
      notifyFinished(finishedMode);
    };
    check();
    // Background tabs throttle intervals to ~1/min, so also fire exactly at
    // `endsAt` and re-check whenever the tab becomes visible.
    const id = window.setInterval(check, 1000);
    const timeout =
      endsAt !== null ? window.setTimeout(check, Math.max(0, endsAt - Date.now()) + 50) : undefined;
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(id);
      if (timeout !== undefined) window.clearTimeout(timeout);
      document.removeEventListener('visibilitychange', check);
    };
  }, [status, endsAt]);

  // Tab title countdown while running/paused; restore the previous title after.
  useEffect(() => {
    if (status === 'idle') return;
    const previous = document.title;
    const write = () => {
      const s = usePomodoroStore.getState();
      document.title = `${formatClock(remainingAt(s, Date.now()))} · ${MODE_LABELS[s.mode]}`;
    };
    write();
    const id = status === 'running' ? window.setInterval(write, 1000) : undefined;
    return () => {
      if (id !== undefined) window.clearInterval(id);
      document.title = previous;
    };
  }, [status, mode, endsAt]);
}
